import { LinkText } from "components/LinkText"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { EventTypeIcon } from "features/events/EventTypeIcon"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { UserProfileModalTrigger } from "features/users/UserProfileModal"
import { useAppSelector } from "integrations/redux/hooks"
import { useRouter } from "pages"
import { CommentText } from "plugins/Toolbox/MapComments/CommentText"
import { CommentDisplay, EventKind } from "@nova/schemas"
import { pluralize } from "@nova/util-strings"
import { ActivityEvent, activityCopy, sectionTimestamp } from "./activityCopy"
import { activityIcon, SystemEventKind } from "./activityIcons"
import { ActorNames } from "./activitySearch"
import { TimelineLine } from "./groupTimeline"

export const ActivitySystemRow = ({
    event,
    actorName,
}: {
    event: ActivityEvent & { kind: SystemEventKind }
    actorName: string | undefined
}) => {
    const icon = activityIcon(event)
    const router = useRouter("/workspace/:workspaceId")
    const project = useAppSelector(selectFireById(event.project_id ?? ""))

    return (
        <div className="flex gap-3 items-center w-full px-2 py-4 rounded-xl">
            <EventTypeIcon source={icon.source} badge={icon.badge} />
            <div className="flex flex-1 min-w-0 items-start gap-2">
                <div className="flex flex-1 flex-col min-w-0 leading-5">
                    <div className="truncate text-white">
                        {activityCopy(event, { actorName: undefined })}
                    </div>
                    <div className="flex min-w-0 text-gray-300">
                        {project?.name && (
                            <router.Link
                                to={{
                                    path: "/project/:projectId",
                                    params: { projectId: project.id },
                                }}
                                className="min-w-0"
                            >
                                <LinkText className="block truncate">{project.name}</LinkText>
                            </router.Link>
                        )}
                        {project?.name && actorName && (
                            <span className="shrink-0">&nbsp;-&nbsp;</span>
                        )}
                        {actorName && (
                            <UserProfileModalTrigger userId={event.created_by} className="min-w-0">
                                <div className="truncate group-hover/UserProfileModalTrigger:text-blue-500 group-hover/UserProfileModalTrigger:underline">
                                    {actorName}
                                </div>
                            </UserProfileModalTrigger>
                        )}
                    </div>
                </div>
                <small className="text-gray-300 shrink-0 leading-5">
                    {sectionTimestamp(event.created_at)}
                </small>
            </div>
        </div>
    )
}

export const ActivityTimelineHeader = ({
    event,
    actorName,
}: {
    event: ActivityEvent
    actorName: string | undefined
}) => (
    <div className="flex gap-3 items-center justify-between w-full px-2 py-4 rounded-xl">
        <UserProfileModalTrigger
            userId={event.created_by}
            className="flex gap-3 items-center min-w-0"
        >
            <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
                <ProfilePhoto userId={event.created_by} fullName={actorName ?? ""} />
            </div>
            <div className="text-white truncate group-hover/UserProfileModalTrigger:text-blue-500 group-hover/UserProfileModalTrigger:underline">
                {actorName ?? "Someone"}
            </div>
        </UserProfileModalTrigger>
        <small className="text-gray-300 shrink-0">{sectionTimestamp(event.created_at)}</small>
    </div>
)

export const ActivityTimelineLine = ({
    line,
    actorNames,
}: {
    line: TimelineLine
    actorNames: ActorNames
}) => {
    const { event, count } = line
    const router = useRouter("/workspace/:workspaceId")
    const project = useAppSelector(selectFireById(event.project_id ?? ""))
    const comment =
        event.kind === EventKind.CommentCreated && event.display?.kind === "comment"
            ? event.display
            : undefined

    if (comment) return <CommentLine comment={comment} actorNames={actorNames} />

    return (
        <div className="flex min-w-0 items-baseline gap-2">
            <div className="truncate text-gray-300">
                {activityCopy(event, {
                    actorName: undefined,
                    nameFor: (id) => actorNames.get(id),
                    count,
                })}
            </div>
            {project?.name && (
                <router.Link
                    to={{
                        path: "/project/:projectId",
                        params: { projectId: project.id },
                    }}
                    className="shrink-0 min-w-0"
                >
                    <LinkText className="block truncate text-gray-400">
                        <small>{project.name}</small>
                    </LinkText>
                </router.Link>
            )}
        </div>
    )
}

export const replyCountLabel = (count: number) =>
    pluralize({ count, single: "Reply", plural: "Replies" })

const CommentLine = ({
    comment,
    actorNames,
}: {
    comment: CommentDisplay
    actorNames: ActorNames
}) => {
    const parentAuthor = comment.parentAuthorId
        ? (actorNames.get(comment.parentAuthorId) ?? "someone")
        : undefined

    return (
        <div className="flex flex-col gap-1 min-w-0">
            {parentAuthor && <div className="text-gray-300">Replied to {parentAuthor}</div>}
            <div className="flex min-w-0 items-baseline gap-2">
                <CommentText text={comment.text} className="text-white line-clamp-3" />
                {comment.replyCount > 0 && (
                    <small className="text-gray-400 shrink-0">
                        {replyCountLabel(comment.replyCount)}
                    </small>
                )}
            </div>
        </div>
    )
}
